import { DIALOGUE_COOLDOWN_S, TICK_RATE } from '../constants.js';
import type { Rng } from '../rng.js';
import type { DialogueCategory, Entity, GameState } from '../types.js';
import { LINES } from './lines.js';

/** Higher wins when scheduling bubbles. */
const PRIORITY: Record<DialogueCategory, number> = {
  kill: 100,
  hurt: 90,
  taunt_hit: 80,
  celebrate: 70,
  match_start: 60,
  low_hp: 50,
  turn_start: 40,
  miss: 20,
  idle: 10,
};

export function dialoguePriority(c: DialogueCategory): number {
  return PRIORITY[c];
}

/**
 * Pick a line for an entity, honoring the per-character cooldown and
 * per-category probability gates. Returns null when nothing should be said.
 * Deterministic — all randomness flows through the sim rng.
 */
export function pickLine(
  state: GameState,
  e: Entity,
  category: DialogueCategory,
  rng: Rng,
  opts: { aimElapsedS?: number } = {},
): { text: string; category: DialogueCategory } | null {
  // Probability gates before we spend the cooldown budget.
  if (category === 'miss' && !rng.chance(0.5)) return null;
  if (category === 'idle') {
    if ((opts.aimElapsedS ?? 0) < 12 || !rng.chance(0.25)) return null;
  }

  const cooldown = DIALOGUE_COOLDOWN_S * TICK_RATE;
  // High-priority lines punch through the cooldown (kills matter).
  const prio = PRIORITY[category];
  const since = state.tick - e.lastBubbleTick;
  if (since < cooldown && prio < 90) return null;
  if (since < 5 && prio < 100) return null;

  const bank = LINES[category][e.accent];
  let idx = Math.floor(rng.next() * bank.length);
  if (bank.length > 1 && idx === e.lastLineIdx) {
    idx = (idx + 1) % bank.length;
  }
  e.lastLineIdx = idx;
  e.lastBubbleTick = state.tick;
  return { text: bank[idx], category };
}
